import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tenantSettingsApi } from '@/api/tenantSettings'
import { useTenant } from '@/contexts/TenantContext'
import { invalidateWorkflowTriggerSchemas } from './useWorkflowDefinitions'
import { TITAN_ENDPOINT_STATUS_KEY } from './useConnectors'
import { useTenantKey } from './useTenantKey'
import { invalidateClaudeAuthAvailability, invalidateCopilotAuthAvailability } from './useProjectSettings'

function useClaudeApiKeyQueryKey() {
  const { currentTenant } = useTenant()
  return ['tenant', 'settings', 'claude-api-key', currentTenant?.id ?? ''] as const
}

export function useTenantClaudeApiKey(enabled = true) {
  const queryKey = useClaudeApiKeyQueryKey()
  return useQuery({
    queryKey,
    queryFn: tenantSettingsApi.getClaudeApiKey,
    enabled,
  })
}

export function useSaveTenantClaudeApiKey() {
  const queryClient = useQueryClient()
  const queryKey = useClaudeApiKeyQueryKey()
  return useMutation({
    mutationFn: (apiKey: string) => tenantSettingsApi.saveClaudeApiKey(apiKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateClaudeAuthAvailability(queryClient)
      invalidateWorkflowTriggerSchemas(queryClient)
    },
  })
}

export function useDeleteTenantClaudeApiKey() {
  const queryClient = useQueryClient()
  const queryKey = useClaudeApiKeyQueryKey()
  return useMutation({
    mutationFn: () => tenantSettingsApi.deleteClaudeApiKey(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateClaudeAuthAvailability(queryClient)
      invalidateWorkflowTriggerSchemas(queryClient)
    },
  })
}

/* ── Copilot ── */

function useCopilotApiKeyQueryKey() {
  const { currentTenant } = useTenant()
  return ['tenant', 'settings', 'copilot-api-key', currentTenant?.id ?? ''] as const
}

export function useTenantCopilotApiKey(enabled = true) {
  const queryKey = useCopilotApiKeyQueryKey()
  return useQuery({
    queryKey,
    queryFn: tenantSettingsApi.getCopilotApiKey,
    enabled,
  })
}

export function useSaveTenantCopilotApiKey() {
  const queryClient = useQueryClient()
  const queryKey = useCopilotApiKeyQueryKey()
  return useMutation({
    mutationFn: (apiKey: string) => tenantSettingsApi.saveCopilotApiKey(apiKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateCopilotAuthAvailability(queryClient)
      invalidateWorkflowTriggerSchemas(queryClient)
    },
  })
}

export function useDeleteTenantCopilotApiKey() {
  const queryClient = useQueryClient()
  const queryKey = useCopilotApiKeyQueryKey()
  return useMutation({
    mutationFn: () => tenantSettingsApi.deleteCopilotApiKey(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateCopilotAuthAvailability(queryClient)
      invalidateWorkflowTriggerSchemas(queryClient)
    },
  })
}


function useTitanSettingsQueryKey() {
  const { currentTenant } = useTenant()
  return ['tenant', 'settings', 'titan-settings', currentTenant?.id ?? ''] as const
}

export function useTenantTitanSettings(enabled = true) {
  const queryKey = useTitanSettingsQueryKey()
  return useQuery({
    queryKey,
    queryFn: tenantSettingsApi.getTitanSettings,
    enabled,
  })
}

export function useSaveTenantTitanSettings() {
  const queryClient = useQueryClient()
  const queryKey = useTitanSettingsQueryKey()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: ({ endpoint, caCertificate }: { endpoint: string; caCertificate: string | null }) =>
      tenantSettingsApi.saveTitanSettings(endpoint, caCertificate),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateWorkflowTriggerSchemas(queryClient)
      queryClient.invalidateQueries({ queryKey: tenantKey(TITAN_ENDPOINT_STATUS_KEY) })
    },
  })
}

export function useDeleteTenantTitanSettings() {
  const queryClient = useQueryClient()
  const queryKey = useTitanSettingsQueryKey()
  const tenantKey = useTenantKey()
  return useMutation({
    mutationFn: () => tenantSettingsApi.deleteTitanSettings(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey })
      invalidateWorkflowTriggerSchemas(queryClient)
      queryClient.invalidateQueries({ queryKey: tenantKey(TITAN_ENDPOINT_STATUS_KEY) })
    },
  })
}
